import { Grid, Stack } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { pick } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BanderoleInfo } from '@work4all/components/lib/components/banderole/BanderoleInfo';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDataMutation } from '@work4all/data';

import { Tenant } from '@work4all/models/lib/Classes/Tenant.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { DatevTenantPickerField } from '../../../../../components/entity-picker/DatevTenantPickerField';
import {
  GeneralSetting,
  useGeneralSetting,
} from '../../../../statistic/util/use-general-setting';
import { ControllerPlus } from '../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { Collapse } from '../../components';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { useFormUpdate } from '../../utils/use-form-update';

type TenantFormValue = PathsOf<Tenant, 2>;

type TenantOverlayControllerProps = MaskControllerProps & {
  amplitudeEntryPoint: string;
};

const DEFAULT_VALUES = {};

export function TenantOverlayController(props: TenantOverlayControllerProps) {
  const { amplitudeEntryPoint } = props;

  const [mutate] = useDataMutation<Tenant, EMode.upsert>({
    entity: Entities.tenant,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
    },
  });

  const mask = useMaskConfig(props);

  useEffect(() => {
    if (mask.isCreateMode) {
      sendAmplitudeData(EventType.AddTenant, {
        entryPoint: amplitudeEntryPoint,
      });
    } else {
      sendAmplitudeData(EventType.EditTenant, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  }, [amplitudeEntryPoint, mask, mask.isCreateMode, props.id]);

  const request = useTenantRequestData(mask.id);

  const overlay = useMaskOverlay<TenantFormValue>({
    ...props,
    request,
    newEntityData: DEFAULT_VALUES,
    mask,
    getSubTitle: (x) => x.name,
    id: Number(mask.id),
  });

  const { goBack } = useHistoryStack();

  const { formState } = overlay.form;

  const handleSubmit = useEventCallback(async (values: TenantFormValue) => {
    const update = mask.isCreateMode
      ? values
      : pick(values, ['id', ...Object.keys(formState.dirtyFields)]);

    await mutate(update);

    sendAmplitudeData(EventType.SaveTenant, {
      entryPoint: amplitudeEntryPoint,
    });
    goBack({ force: true });
  });

  useFormUpdate(
    {
      datevTenant: (datevTenant) => {
        return {
          datevClientNumber: datevTenant?.clientNumber || 0,
        };
      },
    },
    overlay.form
  );

  return (
    <OverlayController<TenantFormValue> {...overlay} onSubmit={handleSubmit}>
      <TenantMaskContent />
    </OverlayController>
  );
}

function TenantMaskContent() {
  const { t } = useTranslation();

  const { register, control } = useFormContextPlus<TenantFormValue>();

  const { data: settingsData } = useGeneralSetting({
    name: GeneralSetting.costCenterAsInvoiceNumberRange,
  });

  const setting = settingsData?.[0];

  const tenantAsCostCenter = Boolean(setting?.value);

  return (
    <>
      {tenantAsCostCenter && (
        <BanderoleInfo
          visible
          text={t('TENANT.TENANT_AS_COSTCENTER.BANDEROLE')}
        />
      )}
      <Collapse title={t('COMMON.GENERAL')} defaultOpen>
        <Stack gap="1rem">
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput label={t('COMMON.NAME')} {...register('name')} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput
                label={t('COMMON.STREET')}
                {...register('street')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.POSTAL_CODE')}
                {...register('postalCode')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput label={t('COMMON.CITY')} {...register('city')} />
            </Grid>
          </Grid>
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.STATE')}
                {...register('country')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.FEDERAL_STATE')}
                {...register('state')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.PHONE_NUMBER')}
                {...register('phoneNumber')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.FAX')}
                {...register('faxNumber')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledInput label={t('COMMON.WEBSITE')} {...register('www')} />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
      <Collapse title={t('COMMON.FIBU_DATA')} defaultOpen>
        <Grid container spacing="1rem">
          <Grid item xs={12} sm={6} md={2}>
            <LabeledInput
              label={t('COMMON.TENANT_NUMBER')}
              {...register('tenantNumber')}
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <LabeledInput
              label={t('COMMON.ADVISOR_NUMBER')}
              {...register('advisorNumber')}
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <LabeledInput
              label={t('COMMON.TAX_NUMBER')}
              {...register('taxNumber')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <LabeledInput label={t('COMMON.UST_ID')} {...register('ustID')} />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <ControllerPlus
              control={control}
              name="datevTenant"
              render={({ field }) => {
                return <DatevTenantPickerField {...field} clearable />;
              }}
            />
          </Grid>
        </Grid>
      </Collapse>
      <Collapse title={t('MASK.COMMENT')} defaultOpen>
        <LabeledInput
          label={t('COMMON.NOTE')}
          {...register('note')}
          multiline
        />
      </Collapse>
    </>
  );
}

export const useTenantRequestData = (id: string | number) => {
  return useMemo<DataRequest>(() => {
    const tenantRequestData: Tenant<EMode.query> = {
      id: null,
      name: null,
      advisorNumber: null,
      city: null,
      country: null,
      faxNumber: null,
      note: null,
      phoneNumber: null,
      postalCode: null,
      state: null,
      stateCode: null,
      street: null,
      taxNumber: null,
      tenantNumber: null,
      www: null,
      ustID: null,
      datevClientNumber: null,
      datevTenant: {
        id: null,
        clientNumber: null,
        consultantNumber: null,
        name: null,
      },
    };
    const filter = [{ id: { $eq: id } }];
    const data = tenantRequestData;
    return {
      entity: Entities.tenant,
      data,
      filter,
      completeDataResponse: true,
    };
  }, [id]);
};
